/** Serialize a synthesized mechanism and its curves for download / sharing. */

import type { MechanismSpec } from "../kinematics/types.js";
import { packParams, paramNames } from "../kinematics/types.js";
import { traceCoupler } from "../kinematics/trace.js";
import type { P2 } from "../synthesis/curve.js";

export function mechanismToJSON(spec: MechanismSpec, meta: Record<string, unknown> = {}): string {
  const params = packParams(spec);
  const names = paramNames(spec);
  return JSON.stringify(
    {
      format: "burmester-mechanism@1",
      generated: new Date().toISOString(),
      ...meta,
      topology: spec.topology,
      grounds: spec.grounds,
      driver: spec.driver,
      bars: spec.bars.map(([a, b, l]) => ({ a, b, length: l })),
      couplerPoint: spec.coupler,
      parameters: Object.fromEntries(names.map((n, i) => [n, params[i]])),
      spec,
    },
    null,
    2,
  );
}

function pathD(pts: P2[], close: boolean): string {
  if (pts.length === 0) return "";
  const d = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(4)} ${p[1].toFixed(4)}`);
  if (close) d.push("Z");
  return d.join(" ");
}

/** A standalone SVG of the mechanism at one pose plus its coupler + target curves. */
export function mechanismToSVG(
  spec: MechanismSpec,
  opts: { target?: P2[] | null; driverAngle?: number } = {},
): string {
  const tr = traceCoupler(spec, { samples: 240 });
  const curve = tr.curve.filter(
    (_, i) => tr.converged[i] && Number.isFinite(tr.curve[i][0]) && Number.isFinite(tr.curve[i][1]),
  );
  const pts: P2[] = [...curve];
  if (opts.target) pts.push(...opts.target);
  for (const g of spec.grounds) pts.push(g);
  // include the joint positions from every converged pose
  tr.solutions.forEach((s, i) => {
    if (tr.converged[i]) for (const p of s.positions) pts.push(p as P2);
  });

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of pts) {
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  if (!Number.isFinite(minX) || maxX - minX < 1e-6 || maxY - minY < 1e-6) {
    minX = -1;
    minY = -1;
    maxX = 1;
    maxY = 1;
  }
  const pad = 0.12 * Math.max(maxX - minX, maxY - minY, 1);
  minX -= pad;
  minY -= pad;
  maxX += pad;
  maxY += pad;
  const w = maxX - minX;
  const h = maxY - minY;
  const sw = w / 320; // stroke width in world units

  const angle = opts.driverAngle ?? 0;
  let sol = traceCoupler(spec, { samples: 1, startAngle: angle, sweep: 0 }).solutions[0];
  if (!sol.converged) {
    const firstOk = tr.solutions.find((_, i) => tr.converged[i]);
    if (firstOk) sol = firstOk;
  }
  const P = sol.positions;
  const G = spec.grounds.length;

  const bars = spec.bars
    .map(([a, b]) => `<path d="${pathD([P[a], P[b]], false)}" stroke="#222" stroke-width="${3 * sw}"/>`)
    .join("");
  const crank = `<path d="${pathD([spec.grounds[spec.driver.base], P[G]], false)}" stroke="#c2410c" stroke-width="${3.5 * sw}"/>`;
  const joints = P.map(
    (p) => `<circle cx="${p[0].toFixed(4)}" cy="${p[1].toFixed(4)}" r="${4 * sw}" fill="#fff" stroke="#222" stroke-width="${1.5 * sw}"/>`,
  ).join("");

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${minX.toFixed(3)} ${(-maxY).toFixed(3)} ${w.toFixed(3)} ${h.toFixed(3)}" width="720" height="${Math.round((720 * h) / w)}">
<g transform="scale(1,-1)">
<rect x="${minX}" y="${minY}" width="${w}" height="${h}" fill="#f7f7f4"/>
${opts.target ? `<path d="${pathD(opts.target, false)}" fill="none" stroke="#059669" stroke-width="${2.5 * sw}" stroke-dasharray="${6 * sw} ${4 * sw}"/>` : ""}
<path d="${pathD(curve, true)}" fill="none" stroke="#1d4ed8" stroke-width="${2 * sw}"/>
${bars}${crank}${joints}
</g>
</svg>`;
}

export function download(filename: string, text: string, mime: string): void {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
